import { Candidate, Job } from '../types';
import { generateMockCandidates } from '../data/mockCandidates';
import { generateMockJobs } from '../data/mockJobs';
import { enhancedSearch } from './searchUtils';
import { addToRecycleBin } from './recycleBin';

type NewCandidate = Omit<Candidate, 'id'>;

export interface Appointment {
  id: string;
  candidateName: string;
  candidateEmail: string;
  candidatePhone: string;
  appointmentType: string;
  date: string;
  time: string;
  location: string;
  interviewer: string;
  status: 'Scheduled' | 'Completed' | 'Cancelled' | 'No Show' | 'Rescheduled';
  candidateStage: 'Interviewing' | 'Awaiting Visa' | 'Cancelled' | 'Deployed' | 'On Hold';
  notes: string;
  createdAt: string;
}

const generateMockAppointments = (): Appointment[] => [
  {
    id: '1',
    candidateName: 'Jane Mwangi',
    candidateEmail: 'jane.mwangi@email.com',
    candidatePhone: '+254712345678',
    appointmentType: 'Initial Interview',
    date: '2024-01-22',
    time: '10:00',
    location: 'Naim Investments Office - Room A',
    interviewer: 'Sarah Johnson',
    status: 'Scheduled',
    candidateStage: 'Interviewing',
    notes: 'Candidate for domestic helper position in Saudi Arabia',
    createdAt: '2024-01-15'
  },
  {
    id: '2',
    candidateName: 'John Kamau',
    candidateEmail: 'john.kamau@email.com',
    candidatePhone: '+254723456789',
    appointmentType: 'Medical Examination',
    date: '2024-01-22',
    time: '14:00',
    location: 'Nairobi Medical Center',
    interviewer: 'Dr. Ahmed',
    status: 'Scheduled',
    candidateStage: 'Interviewing',
    notes: 'Pre-deployment medical checkup for Kuwait position',
    createdAt: '2024-01-16'
  }
];

const STORAGE_KEYS = {
  CANDIDATES: 'recruitment_crm_candidates',
  JOBS: 'recruitment_crm_jobs',
  APPOINTMENTS: 'recruitment_crm_appointments',
  CV_DRAFTS: 'recruitment_crm_cv_drafts',
  CURRENT_CV_DRAFT: 'recruitment_crm_current_cv_draft',
  TASKS: 'recruitment_crm_tasks',
  INITIALIZED: 'recruitment_crm_initialized'
} as const;

export class LocalStorageManager {
  
  // Initialize data if first time
  static initializeData(): void {
    const isInitialized = localStorage.getItem(STORAGE_KEYS.INITIALIZED);
    
    if (!isInitialized) {
      // First time setup - populate with mock data
      const mockCandidates = generateMockCandidates();
      const mockJobs = generateMockJobs();
      const mockAppointments = generateMockAppointments();
      
      localStorage.setItem(STORAGE_KEYS.CANDIDATES, JSON.stringify(mockCandidates));
      localStorage.setItem(STORAGE_KEYS.JOBS, JSON.stringify(mockJobs));
      localStorage.setItem(STORAGE_KEYS.APPOINTMENTS, JSON.stringify(mockAppointments));
      localStorage.setItem(STORAGE_KEYS.INITIALIZED, 'true');
      
      console.log('📦 Initialized localStorage with mock data');
    }
  }

  // Candidates CRUD operations
  static getCandidates(): Candidate[] {
    this.initializeData();
    const data = localStorage.getItem(STORAGE_KEYS.CANDIDATES);
    return data ? JSON.parse(data) : [];
  }

  static saveCandidates(candidates: Candidate[]): void {
    localStorage.setItem(STORAGE_KEYS.CANDIDATES, JSON.stringify(candidates));
  }

  static addCandidate(candidate: NewCandidate): Candidate | null {
    const candidates = this.getCandidates();
    
    // Normalize data for comparison
    const normalizedNew = {
      email: candidate.email?.toLowerCase().trim(),
      phone: candidate.phone?.replace(/\D/g, ''), // Remove non-digits
      passportNumber: candidate.passportNumber?.toUpperCase().trim(),
      name: candidate.name?.trim().toLowerCase()
    };
    
    // Check for duplicates
    const duplicate = candidates.find((c) => {
      const normalizedExisting = {
        email: c.email?.toLowerCase().trim(),
        phone: c.phone?.replace(/\D/g, ''),
        passportNumber: c.passportNumber?.toUpperCase().trim(),
        name: c.name?.trim().toLowerCase()
      };
      
      return (
        (normalizedNew.email && normalizedNew.email.length > 0 && normalizedExisting.email === normalizedNew.email) ||
        (normalizedNew.passportNumber && normalizedNew.passportNumber.length > 0 && normalizedExisting.passportNumber === normalizedNew.passportNumber) ||
        (normalizedNew.phone && normalizedNew.phone.length > 0 && normalizedExisting.phone === normalizedNew.phone) ||
        (normalizedNew.name && normalizedNew.name.length > 0 && normalizedExisting.name === normalizedNew.name)
      );
    });
    
    if (duplicate) {
      console.warn('Duplicate candidate detected:', { existing: duplicate, new: candidate });
      // Return null to indicate a duplicate was found
      return null;
    }
    
    // Create new candidate if no duplicate found
    const newCandidate: Candidate = {
      ...candidate,
      id: `candidate_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      createdAt: new Date().toISOString()
    };
    
    candidates.push(newCandidate);
    this.saveCandidates(candidates);
    
    // Dispatch event for real-time updates
    window.dispatchEvent(new CustomEvent('candidateAdded', { 
      detail: newCandidate 
    }));
    
    return newCandidate;
  }

  static findOrCreateCandidate(candidateData: Partial<Candidate>): Candidate {
    const candidates = this.getCandidates();
    
    // Normalize data for comparison
    const normalizedNew = {
      email: candidateData.email?.toLowerCase().trim(),
      phone: candidateData.phone?.replace(/\D/g, ''),
      passportNumber: candidateData.passportNumber?.toUpperCase().trim(),
      name: candidateData.name?.trim().toLowerCase()
    };
    
    // Try to find existing candidate
    const existingCandidate = candidates.find((c) => {
      const normalizedExisting = {
        email: c.email?.toLowerCase().trim(),
        phone: c.phone?.replace(/\D/g, ''),
        passportNumber: c.passportNumber?.toUpperCase().trim(),
        name: c.name?.trim().toLowerCase()
      };
      
      return (
        (normalizedNew.passportNumber && normalizedExisting.passportNumber === normalizedNew.passportNumber) ||
        (normalizedNew.email && normalizedExisting.email === normalizedNew.email) ||
        (normalizedNew.phone && normalizedExisting.phone === normalizedNew.phone)
      );
    });
    
    if (existingCandidate) {
      // Update existing candidate with new data
      const updatedCandidate = this.updateCandidate(existingCandidate.id, {
        ...existingCandidate,
        ...candidateData,
        id: existingCandidate.id // Preserve the original ID
      });
      
      console.log('📝 Updated existing candidate:', updatedCandidate?.name);
      return updatedCandidate || existingCandidate;
    }
    
    // Create new candidate if not found
    const { id, ...candidateWithoutId } = candidateData as Candidate;
    return this.addCandidate(candidateWithoutId);
  }

  static updateCandidate(id: string, updates: Partial<Candidate>): Candidate | null {
    const candidates = this.getCandidates();
    const index = candidates.findIndex(c => c.id === id || (c as any)._id === id);
    
    if (index === -1) {
      console.error('❌ Candidate not found:', id);
      return null;
    }
    
    candidates[index] = { ...candidates[index], ...updates };
    localStorage.setItem(STORAGE_KEYS.CANDIDATES, JSON.stringify(candidates));
    
    console.log('📝 Updated candidate:', candidates[index].name);
    return candidates[index];
  }

  static deleteCandidate(id: string): boolean {
    const candidates = this.getCandidates();
    console.log('DEBUG: deleteCandidate - ID to delete:', id);
    console.log('DEBUG: deleteCandidate - Current candidates in storage:', candidates.map(c => c.id));
    const index = candidates.findIndex(c => c.id === id || (c as any)._id === id);

    if (index === -1) {
      console.error('❌ Candidate not found in storage for deletion:', id);
      return false;
    }

    const deletedCandidate = candidates[index];

    // Add to recycle bin before deleting
    addToRecycleBin({
      type: 'candidate',
      name: deletedCandidate.name,
      details: `Stage: ${deletedCandidate.stage} | Job: ${deletedCandidate.jobTitle || 'N/A'}`,
      deletedBy: 'Current User',
      data: deletedCandidate
    });

    candidates.splice(index, 1);
    localStorage.setItem(STORAGE_KEYS.CANDIDATES, JSON.stringify(candidates));

    console.log('🗑️ Candidate moved to recycle bin:', deletedCandidate.name);
    return true;
  }

  static getCandidateById(id: string): Candidate | null {
    const candidates = this.getCandidates();
    return candidates.find(c => c.id === id || (c as any)._id === id) || null;
  }

  // CV Draft operations
  static saveCurrentCVDraft(formData: any, template: string): void {
    try {
      localStorage.setItem(STORAGE_KEYS.CURRENT_CV_DRAFT, JSON.stringify({ formData, template }));
      console.log('💾 Current CV draft saved.');
    } catch (error) {
      console.error('Error saving current CV draft:', error);
    }
  }

  static getCurrentCVDraft(): { formData: any; template: string } | null {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CURRENT_CV_DRAFT);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error('Error loading current CV draft:', error);
      return null;
    }
  }

  static addCVDraft(draft: any): void {
    try {
      const drafts = LocalStorageManager.getCVDrafts();
      drafts.push(draft);
      localStorage.setItem(STORAGE_KEYS.CV_DRAFTS, JSON.stringify(drafts));
      console.log('📝 CV draft added to drafts list. Total:', drafts.length);
    } catch (error) {
      console.error('Error adding CV draft:', error);
    }
  }

  static getCVDrafts(): any[] {
    try {
      const data = localStorage.getItem(STORAGE_KEYS.CV_DRAFTS);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Error loading CV drafts:', error);
      return [];
    }
  }

  static getCVDraftById(id: string): any | null {
    try {
      const drafts = LocalStorageManager.getCVDrafts();
      return drafts.find(draft => draft.id === id || draft.name === id) || null;
    } catch (error) {
      console.error('Error getting CV draft by ID:', error);
      return null;
    }
  }

  static deleteCVDraft(draftIdOrName: string): boolean {
    try {
      let drafts = LocalStorageManager.getCVDrafts();
      const draftToDelete = drafts.find(draft => draft.id === draftIdOrName || draft.name === draftIdOrName);
      
      if (!draftToDelete) {
        console.warn(`⚠️ CV draft not found for deletion: ${draftIdOrName}`);
        return false;
      }

      // Add to recycle bin before deleting
      addToRecycleBin({
        type: 'draft',
        name: draftToDelete.name,
        details: `CV draft for ${draftToDelete.candidateName || 'Unknown Candidate'}`,
        deletedBy: 'Current User',
        data: draftToDelete
      });

      // Remove from drafts
      drafts = drafts.filter(draft => draft.id !== draftToDelete.id);
      localStorage.setItem(STORAGE_KEYS.CV_DRAFTS, JSON.stringify(drafts));
      
      console.log(`🗑️ CV draft moved to recycle bin: ${draftToDelete.name}`);
      return true;
    } catch (error) {
      console.error('Error deleting CV draft:', error);
      return false;
    }
  }

  // Jobs CRUD operations
  static getJobs(): Job[] {
    this.initializeData();
    const data = localStorage.getItem(STORAGE_KEYS.JOBS);
    return data ? JSON.parse(data) : [];
  }

  static addJob(job: Omit<Job, 'id'>): Job {
    const jobs = this.getJobs();
    const newJob: Job = {
      ...job,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    };
    
    jobs.push(newJob);
    localStorage.setItem(STORAGE_KEYS.JOBS, JSON.stringify(jobs));
    
    console.log('✅ Added job:', newJob.title);
    return newJob;
  }

  static updateJob(id: string, updates: Partial<Job>): Job | null {
    const jobs = this.getJobs();
    const index = jobs.findIndex(j => j.id === id);
    
    if (index === -1) {
      console.error('❌ Job not found:', id);
      return null;
    }
    
    jobs[index] = { ...jobs[index], ...updates };
    localStorage.setItem(STORAGE_KEYS.JOBS, JSON.stringify(jobs));
    
    console.log('📝 Updated job:', jobs[index].title);
    return jobs[index];
  }

  static deleteJob(id: string): boolean {
    const jobs = this.getJobs();
    console.log('DEBUG: deleteJob - ID to delete:', id);
    console.log('DEBUG: deleteJob - Current jobs in storage:', jobs.map(j => j.id));
    const index = jobs.findIndex(j => j.id === id);
    
    if (index === -1) {
      console.error('❌ Job not found in storage for deletion:', id);
      return false;
    }
    
    const deletedJob = jobs[index];
    
    // Add to recycle bin before deleting
    addToRecycleBin({
      type: 'job',
      name: deletedJob.title,
      details: `${deletedJob.company} | ${deletedJob.location} | ${deletedJob.type} | Status: ${deletedJob.status}`,
      deletedBy: 'Current User',
      data: deletedJob
    });
    
    jobs.splice(index, 1);
    localStorage.setItem(STORAGE_KEYS.JOBS, JSON.stringify(jobs));
    
    console.log('🗑️ Job moved to recycle bin:', deletedJob.title);
    return true;
  }

  static getJobById(id: string): Job | null {
    const jobs = this.getJobs();
    return jobs.find(j => j.id === id) || null;
  }

  // Appointments CRUD operations
  static getAppointments(): Appointment[] {
    this.initializeData();
    const data = localStorage.getItem(STORAGE_KEYS.APPOINTMENTS);
    return data ? JSON.parse(data) : [];
  }

  static addAppointment(appointment: Omit<Appointment, 'id'>): Appointment {
    const appointments = this.getAppointments();
    const newAppointment: Appointment = {
      ...appointment,
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9)
    };
    
    appointments.push(newAppointment);
    localStorage.setItem(STORAGE_KEYS.APPOINTMENTS, JSON.stringify(appointments));
    
    console.log('✅ Added appointment:', newAppointment.candidateName);
    return newAppointment;
  }

  static updateAppointment(id: string, updates: Partial<Appointment>): Appointment | null {
    const appointments = this.getAppointments();
    const index = appointments.findIndex(a => a.id === id);
    
    if (index === -1) {
      console.error('❌ Appointment not found:', id);
      return null;
    }
    
    appointments[index] = { ...appointments[index], ...updates };
    localStorage.setItem(STORAGE_KEYS.APPOINTMENTS, JSON.stringify(appointments));
    
    console.log('📝 Updated appointment:', appointments[index].candidateName);
    return appointments[index];
  }

  static deleteAppointment(id: string): boolean {
    const appointments = this.getAppointments();
    console.log('DEBUG: deleteAppointment - ID to delete:', id);
    console.log('DEBUG: deleteAppointment - Current appointments in storage:', appointments.map(a => a.id));
    const index = appointments.findIndex(a => a.id === id);
    
    if (index === -1) {
      console.error('❌ Appointment not found in storage for deletion:', id);
      return false;
    }
    
    const deletedAppointment = appointments[index];
    
    // Add to recycle bin before deleting
    addToRecycleBin({
      type: 'appointment',
      name: `${deletedAppointment.candidateName} - ${deletedAppointment.appointmentType}`,
      details: `${deletedAppointment.appointmentType} appointment on ${deletedAppointment.date} at ${deletedAppointment.time}`,
      deletedBy: 'Current User',
      data: deletedAppointment
    });
    
    appointments.splice(index, 1);
    localStorage.setItem(STORAGE_KEYS.APPOINTMENTS, JSON.stringify(appointments));
    
    console.log('🗑️ Appointment moved to recycle bin:', deletedAppointment.candidateName);
    return true;
  }

  static getAppointmentById(id: string): Appointment | null {
    const appointments = this.getAppointments();
    return appointments.find(a => a.id === id) || null;
  }

  // Utility methods
  static resetData(): void {
    localStorage.removeItem(STORAGE_KEYS.CANDIDATES);
    localStorage.removeItem(STORAGE_KEYS.JOBS);
    localStorage.removeItem(STORAGE_KEYS.APPOINTMENTS);
    localStorage.removeItem(STORAGE_KEYS.TASKS);
    localStorage.removeItem(STORAGE_KEYS.INITIALIZED);
    localStorage.removeItem(STORAGE_KEYS.CV_DRAFTS);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_CV_DRAFT);
    
    console.log('🔄 Reset all data - will reinitialize on next access');
  }

  static exportData() {
    const candidates = this.getCandidates();
    const jobs = this.getJobs();
    const appointments = this.getAppointments();
    
    return {
      candidates,
      jobs,
      appointments,
      exportDate: new Date().toISOString(),
      version: '1.0'
    };
  }

  static importData(data: { candidates: Candidate[], jobs: Job[], appointments?: Appointment[] }): void {
    localStorage.setItem(STORAGE_KEYS.CANDIDATES, JSON.stringify(data.candidates));
    localStorage.setItem(STORAGE_KEYS.JOBS, JSON.stringify(data.jobs));
    if (data.appointments) {
      localStorage.setItem(STORAGE_KEYS.APPOINTMENTS, JSON.stringify(data.appointments));
    }
    localStorage.setItem(STORAGE_KEYS.INITIALIZED, 'true');
    
    console.log('📥 Imported data successfully');
  }

  // Search and filter utilities
  static searchCandidates(query: string): Candidate[] {
    const candidates = this.getCandidates();
    
    return candidates.filter(candidate => {
      const searchableFields = [
        candidate.name,
        candidate.email,
        candidate.phone,
        candidate.jobTitle || '',
        candidate.company || '',
        candidate.stage,
        candidate.countryApplyingTo || '',
        candidate.passportNumber || '',
        candidate.notes || '',
        candidate.city || '',
        (candidate.salary || '').toString()
      ];
      
      return enhancedSearch(query, searchableFields);
    });
  }

  static searchJobs(query: string): Job[] {
    const jobs = this.getJobs();
    const lowercaseQuery = query.toLowerCase();
    
    return jobs.filter(job => 
      job.title.toLowerCase().includes(lowercaseQuery) ||
      job.company.toLowerCase().includes(lowercaseQuery) ||
      job.location.toLowerCase().includes(lowercaseQuery) ||
      job.type.toLowerCase().includes(lowercaseQuery) ||
      job.status.toLowerCase().includes(lowercaseQuery)
    );
  }

  static filterCandidatesByStage(stage: string): Candidate[] {
    const candidates = this.getCandidates();
    return stage === 'All' ? candidates : candidates.filter(c => c.stage === stage);
  }

  static filterJobsByStatus(status: string): Job[] {
    const jobs = this.getJobs();
    return status === 'All' ? jobs : jobs.filter(j => j.status === status);
  }

  static searchAppointments(query: string): Appointment[] {
    const appointments = this.getAppointments();
    
    return appointments.filter(appointment => {
      const searchableFields = [
        appointment.candidateName,
        appointment.candidateEmail,
        appointment.candidatePhone,
        appointment.appointmentType,
        appointment.location,
        appointment.interviewer,
        appointment.status,
        appointment.candidateStage,
        appointment.notes || '',
        appointment.date,
        appointment.time
      ];
      
      return enhancedSearch(query, searchableFields);
    });
  }

  static filterAppointmentsByStatus(status: string): Appointment[] {
    const appointments = this.getAppointments();
    return status === 'all' ? appointments : appointments.filter(a => a.status === status);
  }

  static filterAppointmentsByStage(stage: string): Appointment[] {
    const appointments = this.getAppointments();
    return stage === 'all' ? appointments : appointments.filter(a => a.candidateStage === stage);
  }

  // Tasks CRUD operations
  static getTasks(): any[] {
    this.initializeData();
    const data = localStorage.getItem(STORAGE_KEYS.TASKS);
    return data ? JSON.parse(data) : [];
  }

  static addTask(task: any): any {
    const tasks = this.getTasks();
    const newTask = {
      ...task,
      id: task.id || Date.now().toString() + Math.random().toString(36).substr(2, 9),
      createdAt: task.createdAt || new Date().toISOString()
    };
    
    tasks.push(newTask);
    localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks));
    
    console.log('✅ Added task:', newTask.title || newTask.name);
    return newTask;
  }

  static updateTask(id: string, updates: any): any | null {
    const tasks = this.getTasks();
    const index = tasks.findIndex(t => t.id === id);
    
    if (index === -1) {
      console.error('❌ Task not found:', id);
      return null;
    }
    
    tasks[index] = { ...tasks[index], ...updates };
    localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks));
    
    console.log('📝 Updated task:', tasks[index].title || tasks[index].name);
    return tasks[index];
  }

  static deleteTask(id: string): boolean {
    const tasks = this.getTasks();
    console.log('DEBUG: deleteTask - ID to delete:', id);
    console.log('DEBUG: deleteTask - Current tasks in storage:', tasks.map(t => t.id));
    const index = tasks.findIndex(t => t.id === id);
    
    if (index === -1) {
      console.error('❌ Task not found in storage for deletion:', id);
      return false;
    }
    
    const deletedTask = tasks[index];
    
    // Add to recycle bin before deleting
    addToRecycleBin({
      type: 'task',
      name: deletedTask.title || deletedTask.name,
      details: `Priority: ${deletedTask.priority || 'Normal'} | Due: ${deletedTask.dueDate || 'No due date'}`,
      deletedBy: 'Current User',
      data: deletedTask
    });
    
    tasks.splice(index, 1);
    localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks));
    
    console.log('🗑️ Task moved to recycle bin:', deletedTask.title || deletedTask.name);
    return true;
  }

  static getTaskById(id: string): any | null {
    const tasks = this.getTasks();
    return tasks.find(t => t.id === id) || null;
  }

  static clearDuplicatesAndReset(): void {
    localStorage.removeItem('recruitment_crm_candidates');
    localStorage.removeItem('cvBuilderCandidates');
    localStorage.removeItem('addedCandidates');
    localStorage.removeItem('recruitment_crm_initialized');
    localStorage.removeItem(STORAGE_KEYS.CV_DRAFTS);
    localStorage.removeItem(STORAGE_KEYS.CURRENT_CV_DRAFT);
    window.location.reload();
  }

  // Perform one-time cleanup with backup
  static performOneTimeCleanup(): void {
    const CLEANUP_KEY = 'candidateCleanupPerformed';
    
    if (localStorage.getItem(CLEANUP_KEY) === 'true') {
      return; // Already performed
    }
    
    // Create backup first
    const backup = {
      timestamp: new Date().toISOString(),
      data: this.getCandidates()
    };
    localStorage.setItem('candidates_backup_before_cleanup', JSON.stringify(backup));
    
    // Run enhanced deduplication
    const beforeCount = this.getCandidates().length;
    this.deduplicateCandidates();
    const afterCount = this.getCandidates().length;
    
    console.log(`Cleanup complete. Removed ${beforeCount - afterCount} duplicates.`);
    
    // Mark cleanup as done
    localStorage.setItem(CLEANUP_KEY, 'true');
  }

  // Enhanced deduplication with better duplicate detection
  static deduplicateCandidates(): void {
    try {
      const candidates = this.getCandidates();
      const uniqueCandidatesMap = new Map<string, Candidate>();
      const processedIdentifiers = new Set<string>();
      
      for (const candidate of candidates) {
        // Normalize candidate data
        const normalized = {
          email: candidate.email?.toLowerCase().trim(),
          phone: candidate.phone?.replace(/\D/g, ''),
          passportNumber: candidate.passportNumber?.toUpperCase().trim(),
          name: candidate.name?.trim().toLowerCase()
        };
        
        // Create multiple keys to check for duplicates
        const keys = [
          normalized.email && `email:${normalized.email}`,
          normalized.passportNumber && `passport:${normalized.passportNumber}`,
          normalized.phone && `phone:${normalized.phone}`,
          normalized.name && normalized.phone && `name-phone:${normalized.name}-${normalized.phone}`
        ].filter(Boolean);
        
        // Check if we've seen any of these identifiers
        let isDuplicate = false;
        for (const key of keys) {
          if (key && processedIdentifiers.has(key)) {
            isDuplicate = true;
            break;
          }
        }
        
        if (!isDuplicate) {
          // This is a unique candidate
          const uniqueKey = keys[0] || `unique_${Date.now()}_${Math.random()}`;
          uniqueCandidatesMap.set(uniqueKey, candidate);
          
          // Mark all identifiers as processed
          keys.forEach(key => {
            if (key) processedIdentifiers.add(key);
          });
        }
      }
      
      const deduplicatedCandidates = Array.from(uniqueCandidatesMap.values());
      localStorage.setItem(STORAGE_KEYS.CANDIDATES, JSON.stringify(deduplicatedCandidates));
      
      console.log(`🧹 Deduplicated candidates: ${candidates.length} -> ${deduplicatedCandidates.length}`);
    } catch (error) {
      console.error('Error deduplicating candidates:', error);
    }
  }

}
